import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import HistoryIcon from "@mui/icons-material/History";
import { Link, useLocation } from "react-router-dom";
import { formatDateToDDMMYY } from "../../../../../utils/helpers";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";

interface FieldMeta {
  field: string;
  headerName: string;
  type?: string;
}

interface TaskDetailPanelProps {
  task: any;
  dataFieldsMeta: FieldMeta[];
  occurrenceFieldsMeta: FieldMeta[];
  onOpenStatusLog: (statusLog: any[]) => void;
}

const KNOWN_STATUSES = ["To Do", "Snoozed", "Snooze", "In Progress", "Done"];

const normalizeStatus = (status: string) =>
  status === "Snooze" ? "Snoozed" : status;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const formatFieldValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : Number(value.toFixed(2));
  }
  if (typeof value === "string" && ISO_DATE_PATTERN.test(value)) {
    return formatDateToDDMMYY(value) || value;
  }
  return value;
};

const TOOLTIP_MIN_LENGTH = 50;

const FieldItem = ({ label, value }: { label: string; value: any }) => {
  const displayValue = formatFieldValue(value);
  const shouldShowTooltip = String(displayValue).length > TOOLTIP_MIN_LENGTH;

  const valueTypography = (
    <Typography
      sx={{
        fontSize: "14px",
        fontWeight: 400,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {displayValue}
    </Typography>
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
        {label}
      </Typography>
      {shouldShowTooltip ? (
        <Tooltip title={displayValue} arrow>
          {valueTypography}
        </Tooltip>
      ) : (
        valueTypography
      )}
    </Box>
  );
};

const fieldGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 2,
};

const sectionHeaderSx = {
  fontSize: "14px",
  fontWeight: 400,
  pb: 1,
  mb: 1.5,
  borderBottom: "1px solid #e0e0e0",
};

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  task,
  dataFieldsMeta,
  occurrenceFieldsMeta,
  onOpenStatusLog,
}) => {
  const location = useLocation();

  if (!task) {
    return (
      <Box
        sx={{
          flex: "1 1 50%",
          minWidth: 0,
          borderLeft: "1px solid #e0e0e0",
          height: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
          Click on a row to see the full details of a specific occurrence.
        </Typography>
      </Box>
    );
  }

  const originalTask = task.originalTask;
  const firstField = dataFieldsMeta[0];
  const remainingDataFields = dataFieldsMeta.slice(1);

  const referenceId = originalTask?.reference_id;
  const previewSearchParams = new URLSearchParams(location.search);
  if (referenceId) {
    previewSearchParams.set("reference_id", referenceId);
  }

  return (
    <Box
      sx={{
        flex: "1 1 50%",
        minWidth: 0,
        borderLeft: "1px solid #e0e0e0",
        height: "100%",
        overflowY: "auto",
        px: 2,
        py: 1.5,
      }}
    >
      {/* General Info */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
              {firstField ? task[firstField.field] || "-" : "-"}
            </Typography>
            {task.site_name && (
              <Chip
                label={task.site_name}
                size="small"
                sx={{ backgroundColor: "#7B1FA2", color: "#ffffff" }}
              />
            )}
            {KNOWN_STATUSES.includes(task.status) ? (
              <Box sx={{ width: 100, flexShrink: 0 }}>
                <DataStatusAlert status={normalizeStatus(task.status)} />
              </Box>
            ) : (
              task.status && (
                <Chip label={task.status} size="small" variant="outlined" />
              )
            )}
          </Stack>
          <Stack sx={{ mt: 1 }} gap={0.5}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                Assignee:
              </Typography>
              {task.assignee?.trim() ? (
                <>
                  <Avatar
                    sx={{
                      width: 18,
                      height: 18,
                      fontSize: "10px",
                      bgcolor: "#7B1FA2",
                    }}
                  >
                    {task.assignee.trim().charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography sx={{ fontSize: "13px" }}>
                    {task.assignee.trim()}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: "13px" }}>-</Typography>
              )}
            </Stack>
            <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
              Updated On:{" "}
              {task.updated ? formatDateToDDMMYY(task.updated) : "-"}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Tooltip title="View Full Content" arrow>
            <IconButton
              size="small"
              component={Link}
              to={`/operations/data_accuracy/task_name/occurrences/?${previewSearchParams.toString()}`}
              target="_blank"
              data-tracker="177"
            >
              <PreviewIcon fontSize="small" color="secondary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="See History Log" arrow>
            <IconButton
              size="small"
              data-tracker="638"
              onClick={() => onOpenStatusLog(originalTask?.status_log || [])}
            >
              <HistoryIcon fontSize="small" color="secondary" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Task Data */}
      <Box
        sx={{
          mt: 1,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Typography sx={sectionHeaderSx}>Task Data</Typography>
        {remainingDataFields.length > 0 ? (
          <Box sx={fieldGridSx}>
            {remainingDataFields.map((field) => (
              <FieldItem
                key={field.field}
                label={field.headerName}
                value={task[field.field]}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            No Data
          </Typography>
        )}
      </Box>

      {/* Task Info */}
      <Box
        sx={{
          mt: 1,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Typography sx={sectionHeaderSx}>Task Info</Typography>
        {occurrenceFieldsMeta.length > 0 ? (
          <Box sx={fieldGridSx}>
            {occurrenceFieldsMeta.map((field) => (
              <FieldItem
                key={field.field}
                label={field.headerName}
                value={task[field.field]}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            No Data
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TaskDetailPanel;
